// =============================================================================
// Session lifecycle telemetry for DEV-9596.
//
// The bug this exists to monitor ("sessions randomly die in the native apps")
// is invisible from inside a single page load: the interesting quantity is how
// long a session survives ACROSS cold starts, and what finally kills it. So the
// record below is deliberately kept in localStorage rather than in memory or
// sessionStorage — sessionStorage dies with the WebView process, which is the
// very event we are trying to measure survival across — and `startedAt` is
// carried forward when a session is restored from the native mirror, so
// `session.lifetime_ms` measures the logical session rather than one page view.
//
// See docs/native-session-persistence-adr.md (Sentry instrumentation).
// =============================================================================

import { countMetric, distributionMetric, setTelemetryContext } from '../../utils/metricsReporting'
import { reportSharedError, reportSharedWarning } from '../../utils/errorReporting'

const RECORD_KEY = 'icc_session_telemetry'
// Survives the session it describes: needed to notice that a *non-guest* account
// silently became a fresh guest, which is the user-visible catastrophe.
const TOMBSTONE_KEY = 'icc_session_last_identity'
const RECORD_VERSION = 1

export type SessionSource =
    | 'login'
    | 'signup'
    | 'social'
    | 'guest'
    | 'guest_upgrade'
    | 'native_restore'
    | 'cookie_resume'

/** Where a transient (indeterminate) failure happened — the "aborts" being counted. */
export type AbortStage = 'renew' | 'profile' | 'native_restore'

type SessionRecord = {
    v: number
    sessionId: string
    startedAt: number
    source: SessionSource
    userId: string
    isGuest: boolean
    renewals: number
    aborts: number
    restores: number
    coldStarts: number
}

type Tombstone = {
    userId: string
    isGuest: boolean
    endedAt: number
    endReason: string
}

const hasStorage = (): boolean => {
    try {
        return typeof window !== 'undefined' && !!window.localStorage
    } catch {
        return false
    }
}

const readJson = <T>(key: string): T | null => {
    if (!hasStorage()) return null
    try {
        const raw = window.localStorage.getItem(key)
        return raw ? (JSON.parse(raw) as T) : null
    } catch {
        return null
    }
}

const writeJson = (key: string, value: unknown): void => {
    if (!hasStorage()) return
    try {
        window.localStorage.setItem(key, JSON.stringify(value))
    } catch {
        // Quota/private-mode failures must not break auth.
    }
}

const removeKey = (key: string): void => {
    if (!hasStorage()) return
    try {
        window.localStorage.removeItem(key)
    } catch {
        // ignore
    }
}

const newSessionId = (): string => {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
    } catch {
        // fall through
    }
    return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

const readRecord = (): SessionRecord | null => {
    const rec = readJson<SessionRecord>(RECORD_KEY)
    if (!rec || rec.v !== RECORD_VERSION || typeof rec.startedAt !== 'number' || !rec.sessionId) return null
    return rec
}

const ageMs = (rec: SessionRecord): number => Math.max(0, Date.now() - rec.startedAt)

/** Common attribute set so every metric can be sliced the same way in Sentry. */
const attrsOf = (rec: SessionRecord) => ({
    source: rec.source,
    isGuest: rec.isGuest,
    restored: rec.restores > 0,
    coldStarts: rec.coldStarts,
})

/**
 * The seed a restore must carry forward so lifetime keeps accruing instead of
 * resetting to zero every time the cookie jar is lost and rebuilt.
 */
export const getSessionSeed = (): { sessionId: string; startedAt: number } | null => {
    const rec = readRecord()
    return rec ? { sessionId: rec.sessionId, startedAt: rec.startedAt } : null
}

export const getCurrentSessionId = (): string | null => readRecord()?.sessionId ?? null

/**
 * Whether this device has ever held a real (non-guest) account. Consulted when
 * the native bridge times out at boot with no cookie: that outcome is only
 * worth treating as "unconfirmed" if there is something to protect — on a
 * fresh install (or a device that has only ever been a guest) a timeout must
 * fall through to normal guest provisioning, or a shell without the bridge
 * would never mint an identity at all.
 */
export const hasEvidenceOfPriorMemberSession = (): boolean => {
    const rec = readRecord()
    if (rec && !rec.isGuest) return true
    const tomb = readJson<Tombstone>(TOMBSTONE_KEY)
    return !!tomb && !tomb.isGuest
}

/**
 * Records a newly-established session. `seed` is supplied when the session was
 * recovered rather than created (native restore, or a surviving cookie), which
 * preserves the original start time and correlation id.
 */
// The user id this page load has already begun a session for. initUser() runs on
// every appResume as well as at boot, and each run re-enters beginSession — so
// without this, every foreground would count as a cold start and inflate
// session.continued / coldStarts / age_at_cold_start_ms by resume frequency.
// A second call for the same user in one page load is a resume: nothing to
// measure, only the provenance tag to keep current.
let begunForUserThisLoad: string | null = null

export const beginSession = (params: {
    source: SessionSource
    userId: string
    isGuest: boolean
    seed?: { sessionId: string; startedAt: number } | null
}): void => {
    const { source, userId, isGuest, seed } = params

    if (begunForUserThisLoad === userId) {
        setTelemetryContext({ 'session.source': source, 'user.id': userId })
        return
    }
    begunForUserThisLoad = userId

    const previous = readRecord()

    // Same user still going: this is a continuation (cold start / restore), not a
    // brand-new session — keep the clock running.
    const continuing =
        seed ??
        (previous && previous.userId === userId
            ? { sessionId: previous.sessionId, startedAt: previous.startedAt }
            : null)

    const rec: SessionRecord = {
        v: RECORD_VERSION,
        sessionId: continuing?.sessionId ?? newSessionId(),
        startedAt: continuing?.startedAt ?? Date.now(),
        source,
        userId,
        isGuest,
        renewals: continuing && previous?.userId === userId ? previous.renewals : 0,
        aborts: continuing && previous?.userId === userId ? previous.aborts : 0,
        restores: continuing && previous?.userId === userId ? previous.restores : 0,
        coldStarts: continuing && previous?.userId === userId ? previous.coldStarts + 1 : 0,
    }
    writeJson(RECORD_KEY, rec)

    // 'user.id' rides along because this runs before the host's own setUser: the host
    // maps it to its user context so these first logs are searchable by user.
    setTelemetryContext({
        'session.id': rec.sessionId,
        'session.source': source,
        'session.isGuest': isGuest,
        'user.id': userId,
    })

    if (continuing) {
        countMetric('session.continued', { source, isGuest })
        distributionMetric('session.age_at_cold_start_ms', ageMs(rec), attrsOf(rec))
    } else {
        countMetric('session.started', { source, isGuest })
    }
    reportSharedWarning(
        continuing ? 'Session continued' : 'Session started',
        { sessionId: rec.sessionId, userId, source, isGuest, ageMs: ageMs(rec), coldStarts: rec.coldStarts },
        { reason: continuing ? 'session_continued' : 'session_started', source },
    )
}

/** A successful token renewal — the signal that a long-lived session is healthy. */
export const noteRenewal = (): void => {
    const rec = readRecord()
    countMetric('session.renewed', rec ? { source: rec.source, isGuest: rec.isGuest } : undefined)
    if (!rec) return
    rec.renewals += 1
    writeJson(RECORD_KEY, rec)
    distributionMetric('session.age_at_renew_ms', ageMs(rec), attrsOf(rec))
}

/**
 * A transient, unresolved outcome — the "aborts" worth alerting on. Deliberately
 * distinct from a session *ending*: an abort means we could not confirm the
 * session, and (post-DEV-9596) explicitly did NOT tear it down.
 */
export const noteAbort = (stage: AbortStage, reason: string): void => {
    const rec = readRecord()
    countMetric('session.abort', { stage, reason, source: rec?.source ?? 'unknown', isGuest: rec?.isGuest ?? false })
    if (rec) {
        rec.aborts += 1
        writeJson(RECORD_KEY, rec)
    }
    reportSharedWarning(
        'Session check aborted (transient, session preserved)',
        { stage, reason, sessionId: rec?.sessionId, abortsThisSession: rec?.aborts, ageMs: rec ? ageMs(rec) : null },
        { reason: `abort_${stage}`, stage },
    )
}

/** A session recovered from the native mirror — the mirror doing the job it exists for. */
export const noteNativeRestore = (mirroredAtMs: number | null): void => {
    countMetric('native_session.restored')
    if (typeof mirroredAtMs === 'number') {
        distributionMetric('native_session.mirror_age_at_restore_ms', Math.max(0, Date.now() - mirroredAtMs))
    }
    const rec = readRecord()
    if (rec) {
        rec.restores += 1
        writeJson(RECORD_KEY, rec)
    }
}

/** Native-store health, kept separate from session outcomes so it can alert on its own. */
export const noteNativeStoreEvent = (
    event: 'write_ok' | 'write_timeout' | 'clear_ok' | 'clear_timeout' | 'unavailable' | 'invalid' | 'origin_mismatch',
    attributes?: Record<string, string | number | boolean>,
): void => {
    countMetric('native_session.store_event', { event, ...attributes })
}

/**
 * Ends the session and reports the headline number: how long it actually lived,
 * sliced by what killed it.
 */
export const endSession = (reason: string): void => {
    // The session is over, so the next beginSession — even for the same user
    // logging straight back in — is a genuine start, not a resume.
    begunForUserThisLoad = null
    const rec = readRecord()
    countMetric('session.ended', { reason, source: rec?.source ?? 'unknown', isGuest: rec?.isGuest ?? false })
    if (!rec) return

    const lifetime = ageMs(rec)
    distributionMetric('session.lifetime_ms', lifetime, { reason, ...attrsOf(rec) })
    reportSharedWarning(
        'Session ended',
        {
            sessionId: rec.sessionId,
            reason,
            lifetimeMs: lifetime,
            lifetimeHours: +(lifetime / 3600000).toFixed(2),
            renewals: rec.renewals,
            aborts: rec.aborts,
            restores: rec.restores,
            coldStarts: rec.coldStarts,
            source: rec.source,
            isGuest: rec.isGuest,
        },
        { reason: `ended_${reason}`, source: rec.source },
    )

    writeJson(TOMBSTONE_KEY, {
        userId: rec.userId,
        isGuest: rec.isGuest,
        endedAt: Date.now(),
        endReason: reason,
    } satisfies Tombstone)
    removeKey(RECORD_KEY)
}

/**
 * The catastrophe alert: a brand-new guest is being minted while the last known
 * identity on this device was a real account that did NOT log out. That is a
 * user silently losing their account — the exact failure DEV-9596 exists to end.
 */
export const noteGuestProvisioned = (newUserId: string): void => {
    const tomb = readJson<Tombstone>(TOMBSTONE_KEY)
    countMetric('session.guest_provisioned')
    if (!tomb || tomb.isGuest || tomb.endReason === 'explicit_logout' || tomb.endReason === 'server_logout_push') return

    countMetric('session.identity_downgrade', { previousEndReason: tomb.endReason })
    reportSharedError(
        'Silent identity downgrade: registered a new guest over a non-guest account',
        new Error('silent_identity_downgrade'),
        {
            previousUserId: tomb.userId,
            previousEndReason: tomb.endReason,
            msSincePreviousEnded: Date.now() - tomb.endedAt,
            newUserId,
        },
        { reason: 'silent_identity_downgrade', previousEndReason: tomb.endReason },
    )
}

/** Clears the tombstone after a deliberate logout so it can't trigger a false downgrade alert. */
export const clearIdentityTombstone = (): void => removeKey(TOMBSTONE_KEY)
